import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        // FFG (24 Aug): the gallery keeps ONE fixed media order now — the
        // selected-first re-sort is dead-switched (ffg_selected_first in
        // product-media-gallery-content.liquid) and chip -> image is a
        // smooth scroll (custom.js, media<->chip block). This wholesale
        // replaceWith existed to APPLY that re-sort; with the order stable
        // the incoming gallery is markup-identical, so replacing only
        // destroyed client state: the scroll position (the tap-a-chip,
        // watch-the-image-flip-back-to-slide-one bug), an open zoom
        // dialog (the 2s self-close), and the shopper's place mid-swipe.
        // Skip it when the media ids match AS A SET; a REAL media change
        // (combined listings swapping products, say) still replaces.
        //
        // SORTED, not in DOM order (26 Aug, the Pirate race, proven with a
        // headless trace): slideshow.js answers a jump of MORE than one
        // slide with a "placeholder + reorder trick" — it physically moves
        // the target slide next to the current one, animates one step,
        // and restores the DOM only after the scroll finishes. A chip tap
        // that jumps two slides (Boy photo -> Girl photo) has the section
        // fetch landing INSIDE that window, so an order-sensitive compare
        // read the transient order, "mismatched", and replaced the gallery
        // — which renders slide one: the shopper's photo snapped back,
        // always to the first photo, only after multi-slide jumps. Order
        // can never justify a replacement anyway: the selected-first sort
        // is dead-switched, so order is fixed per visit and only the SET
        // says whether this is truly different media.
        const ffgIds = (root) =>
          [...root.querySelectorAll('slideshow-slide [data-media-id]')]
            .map((el) => el.getAttribute('data-media-id'))
            .sort()
            .join();
        if (ffgIds(this) && ffgIds(this) === ffgIds(newMediaGallery)) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
